// import {Select} from 'formik-mui';
import {Box, Button, MenuItem, TextField, Typography} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import clsx from 'clsx';
import React from 'react';
import {
  ActionType,
  FieldValue,
  OptionValueType,
  SelectField,
  SelectGroupField,
  SelectOption,
} from './AutoFieldNew';
import './AutoSelectStyles.css';
import {AutoFieldDebug} from './AutoFieldDebug';
import {SaltusAny} from '../../utils/SaltusAny';
import {useAutoField} from './useAutoForm';
import {UPDATES} from '../../shared/UPDATES';

interface Props {
  field: SelectField | SelectGroupField;
  controlledValue: FieldValue | undefined;
  setControlledValue: React.Dispatch<React.SetStateAction<FieldValue | undefined>>;
  handleFieldSubmit: (value?: FieldValue) => void;
  loading?: boolean;
  disabled: boolean;
  onBlur?: (x: SaltusAny) => SaltusAny;
  readOnly: boolean;
  emptyValText: string;
  error?: boolean;
}

type OptionGroupType = SelectOption & {isGroupTitle?: boolean};

export default function AutoSelect({
  field,
  controlledValue,
  setControlledValue,
  handleFieldSubmit,
  loading,
  onBlur,
  emptyValText,
  disabled,
  readOnly,
  error,
}: Props) {
  const [formikField, formikMeta, formikHelpers] = useAutoField<FieldValue>(field.id);

  const options: OptionGroupType[] =
    field.type === ActionType.select
      ? field.options
      : field.optionGroups.reduce((acc, curr) => {
          return [
            ...acc,
            {label: curr.groupLabel, isGroupTitle: true, value: curr.groupLabel},
            ...curr.options,
          ];
        }, [] as OptionGroupType[]);

  const groupOptions = field.type === ActionType.selectGroup ? field.optionGroups : null;

  const handleSelect = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: SaltusAny;
    }>
  ) => {
    if (field.requireSubmit) {
      handleFieldSubmit(event.target.value as string);
    } else {
      setControlledValue(event.target.value as string);
    }
  };
  const value =
    ((field.requireSubmit ? controlledValue?.toString() : formikField.value) ||
      field.initialValue) ??
    emptyValText;
  return (
    <FormControl
      className={clsx({
        'select-readOnly': readOnly,
        readOnly,
        editable: !readOnly,
      })}
      fullWidth
      error={error || !!formikMeta.error}
      size={field.size || 'small'}
      id={field.id}
    >
      <AutoFieldDebug>{field.id}</AutoFieldDebug>
      <TextField
        data-testid="category-select"
        select
        error={error || !!formikMeta.error}
        variant="outlined"
        onBlur={formikField.onBlur}
        id={field.id}
        size={field.size || 'small'}
        name={field.id}
        label={field.label}
        inputProps={{
          id: field.id,
          readOnly: field.readOnly,
          'data-select-input': field.label,
        }}
        SelectProps={{
          MenuProps: {
            classes: {
              root: field.label.replaceAll(' ', '').toLocaleLowerCase(),
            },
          },
        }}
        InputLabelProps={{
          size: 'small',
        }}
        // style={{color: 'black'}}
        data-testmui={field.label}
        disabled={field.disabled || disabled}
        value={value}
        onChange={(x) => {
          if (readOnly) {
            return;
          }
          if (field.requireSubmit) {
            handleSelect(x);
          } else {
            formikHelpers.setValue(UPDATES._4700, x.target.value as FieldValue);
          }
          if (onBlur) {
            onBlur({});
          }
        }}
      >
        {options
          .filter((option) => {
            if (readOnly) {
              const selected = value === option.value;
              return selected;
            }
            return true;
          })
          .map((option) => (
            <MenuItem
              data-testid={`option-${option.label.replaceAll(' ', '-')}`}
              disabled={option.isGroupTitle}
              selected={value === option.value}
              data-test={option.label}
              key={getSelectOptionValue(option)}
              value={option.value as any}
            >
              {option.label}
              {option.isGroupTitle ? ':' : ''}
            </MenuItem>
          ))}
      </TextField>
      {formikMeta.touched && formikMeta.error ? (
        <Typography
          color="error"
          variant="body2"
          style={{fontSize: '0.7rem', marginTop: 4, marginLeft: 8}}
        >
          {formikMeta.error}
        </Typography>
      ) : null}
      {field.requireSubmit && (controlledValue || formikField.value) && (
        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleFieldSubmit(controlledValue || formikField.value)}
          >
            {loading ? 'Loading...' : 'Confirm'}
          </Button>
        </Box>
      )}
    </FormControl>
  );
}

export function getSelectOptionValue(
  option: SelectOption<OptionValueType>
): string | number {
  // if not a boolean value, key is the value. if its a boolean, convert to text 'true' or 'false' just for the key
  const key =
    // eslint-disable-next-line no-nested-ternary
    typeof option.value !== 'boolean' ? option.value : option.value ? 'true' : 'false';
  return key;
}
